import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import StarField from "@/components/StarField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, PhoneCall } from "lucide-react";
import { PRAYER_LINES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", request: "", urgent: false };

export default function PrayerLinePage() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.request.trim().length < 3) {
      toast.error("Please share your name and prayer request.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        request: form.request.trim(),
        urgent: form.urgent,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      await axios.post(`${API}/prayer-requests`, payload);
      toast.success("Your prayer request is received. You are not alone.");
      setSubmitted(true);
      setForm(empty);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="prayer-line-page"
      className="relative min-h-screen overflow-hidden pt-32 pb-24 px-6 md:px-10 rts-grain"
    >
      <StarField count={80} />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]"
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-5xl mx-auto grid md:grid-cols-12 gap-14">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono-rts text-[0.7rem] tracking-[0.4em] uppercase text-[#EAB308] mb-6"
          >
            Prayer Line
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif-rts text-4xl sm:text-5xl md:text-6xl font-light leading-tight"
            data-testid="prayer-line-title"
          >
            When the road gets{" "}
            <span className="italic text-[#EAB308]">heavy</span>, ask for
            prayer.
          </motion.h1>
          <p className="mt-6 text-white/70 leading-relaxed">
            Send us your request &mdash; a burden, a name, a moment, a battle.
            Someone will lift it up in prayer for you.
          </p>

          <div className="mt-10 rts-glass rounded-xl p-6 flex items-start gap-4">
            <PhoneCall className="w-5 h-5 text-[#EAB308] mt-1" />
            <div>
              <p className="font-mono-rts text-[0.7rem] tracking-[0.3em] uppercase text-white/60">
                Every request is read
              </p>
              <p className="mt-1 text-sm text-white/80">
                Nothing you share is small. Nothing is too late.
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {PRAYER_LINES.map((line, idx) => (
              <a
                key={line.tel}
                href={line.tel}
                className="block rts-glass rounded-xl p-6 border border-[#EAB308]/30 hover:border-[#EAB308]/60 transition-colors duration-300 group"
                data-testid={idx === 0 ? "prayerline-phone-card" : "prayerline-phone-card-crossroads"}
              >
                <p className="font-mono-rts text-[0.7rem] tracking-[0.35em] uppercase text-[#EAB308]">
                  {idx === 0 ? "Or call the Prayer Line" : line.label}
                </p>
                <p
                  className="mt-3 font-serif-rts text-3xl md:text-4xl font-light text-white group-hover:text-[#EAB308] transition-colors duration-300"
                  data-testid={idx === 0 ? "prayerline-phone-number" : "prayerline-phone-number-crossroads"}
                >
                  {line.display}
                </p>
                <p className="mt-2 text-xs text-white/50 font-mono-rts tracking-wider">
                  Tap to call &middot; someone will pray with you
                </p>
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="rts-glass rounded-2xl p-10 text-center"
              data-testid="prayer-success-card"
              style={{
                boxShadow:
                  "0 0 60px rgba(234,179,8,0.15), inset 0 0 0 1px rgba(234,179,8,0.25)",
              }}
            >
              <CheckCircle2 className="w-14 h-14 text-[#EAB308] mx-auto mb-6" />
              <h2 className="font-serif-rts text-3xl md:text-4xl font-light">
                Your request is with us.
              </h2>
              <p className="mt-4 text-white/70 max-w-md mx-auto">
                We&rsquo;re praying for you. Take a breath. The road home is
                still open.
              </p>
              <button
                className="mt-8 rts-btn-primary"
                data-testid="prayer-send-another-btn"
                onClick={() => setSubmitted(false)}
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onSubmit={handleSubmit}
              className="rts-glass rounded-2xl p-8 md:p-10 space-y-6"
              data-testid="prayer-form"
              noValidate
            >
              <div>
                <Label
                  htmlFor="name"
                  className="text-[0.7rem] uppercase tracking-[0.28em] text-white/70"
                >
                  Your Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Who is asking for prayer?"
                  className="mt-2 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 h-11 rounded-md"
                  data-testid="prayer-input-name"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="email"
                    className="text-[0.7rem] uppercase tracking-[0.28em] text-white/70"
                  >
                    Email (optional)
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-2 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 h-11 rounded-md"
                    data-testid="prayer-input-email"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="phone"
                    className="text-[0.7rem] uppercase tracking-[0.28em] text-white/70"
                  >
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+1 (555) 000-0000"
                    className="mt-2 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 h-11 rounded-md"
                    data-testid="prayer-input-phone"
                  />
                </div>
              </div>

              <div>
                <Label
                  htmlFor="request"
                  className="text-[0.7rem] uppercase tracking-[0.28em] text-white/70"
                >
                  Prayer Request
                </Label>
                <Textarea
                  id="request"
                  name="request"
                  required
                  rows={6}
                  value={form.request}
                  onChange={update("request")}
                  placeholder="Share as much or as little as you'd like..."
                  className="mt-2 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 rounded-md resize-none"
                  data-testid="prayer-input-request"
                />
              </div>

              <label
                className="flex items-center gap-3 cursor-pointer select-none"
                data-testid="prayer-urgent-label"
              >
                <Checkbox
                  checked={form.urgent}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, urgent: Boolean(v) }))
                  }
                  className="border-white/20 data-[state=checked]:bg-[#EAB308] data-[state=checked]:border-[#EAB308]"
                  data-testid="prayer-urgent-checkbox"
                />
                <span className="text-[0.75rem] uppercase tracking-[0.22em] text-white/70">
                  This is urgent
                </span>
              </label>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-white/40 font-mono-rts tracking-wider">
                  Your request stays private.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rts-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="prayer-submit-btn"
                >
                  {submitting ? "Sending..." : "Send Prayer Request"}
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </div>
    </main>
  );
}
